import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api.jsx';

const AddUserModal = ({ show, onClose, onUserAdded }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addingUserId, setAddingUserId] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!show) {
      setSearchTerm('');
      setResults([]);
      setError('');
    }
  }, [show]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (searchTerm.trim().length < 2) {
      setResults([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get('/admin/users/search', { params: { q: searchTerm.trim() } });
        setResults(res.data || []);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to search Azure AD');
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => clearTimeout(debounceRef.current);
  }, [searchTerm]);

  const handleAdd = async (result) => {
    setAddingUserId(result.userId);
    setError('');
    try {
      await api.post('/admin/users', {
        userId: result.userId,
        email: result.email,
        displayName: result.displayName
      });
      setResults((prev) => prev.map((item) => (
        item.userId === result.userId ? { ...item, alreadyAdded: true } : item
      )));
      onUserAdded?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add user');
    } finally {
      setAddingUserId(null);
    }
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
      justifyContent: 'center', alignItems: 'center', zIndex: 2000
    }}>
      <div style={{
        backgroundColor: 'white', padding: '30px', borderRadius: '8px',
        maxWidth: '520px', width: '90%', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
      }}>
        <h2 style={{ marginTop: 0 }}>Add User</h2>
        <p style={{ color: '#555', marginBottom: '12px' }}>
          Search Azure AD to add a user. New users are assigned the "user" role by default.
        </p>
        <input
          type="text"
          className="admin-user-search"
          placeholder="Search by name or email"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: '100%', marginBottom: '12px' }}
          autoFocus
        />

        {error && <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>}
        {loading && <div>Searching...</div>}

        {!loading && results.length > 0 && (
          <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px' }}>
            {results.map((result) => (
              <div
                key={result.userId}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 12px', borderBottom: '1px solid #eee'
                }}
              >
                <div>
                  <div>{result.displayName || result.email}</div>
                  <div style={{ fontSize: '12px', color: '#777' }}>
                    {result.email}
                    {result.accountEnabled === false && ' \u2022 disabled in Azure AD'}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={result.alreadyAdded || addingUserId === result.userId}
                  onClick={() => handleAdd(result)}
                >
                  {result.alreadyAdded ? 'Added' : (addingUserId === result.userId ? 'Adding...' : 'Add')}
                </button>
              </div>
            ))}
          </div>
        )}

        {!loading && searchTerm.trim().length >= 2 && results.length === 0 && !error && (
          <div style={{ color: '#777' }}>No matching Azure AD users found.</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onClose} className="btn btn-secondary">Close</button>
        </div>
      </div>
    </div>
  );
};

export default AddUserModal;
